import React, { useState, useEffect } from 'react';
import { cryptoPairLabel, isCryptoSymbol } from '../composables/fetchCrypto';
import { formatCryptoUsd } from '../utils/cryptoDecimal';
import { getStockEmoji } from '../styles/StockSymbolEmoji';
import { fetchPolygonSnapshotPrice,fetchPolygonSnapshotPriceses } from '../composables/fetchPolygon';
import { getProgressBarColor } from '../composables/colorUtils';

interface AverageCalculatePopupProps {
  open: boolean;
  onClose: () => void;
  symbol: string;
  currentPrice: number;
  currentShares: number;
  useCustomPrice: boolean; // เพิ่ม
  customPrice: number | null; // เพิ่ม
  currentCost: number;
  totalValueInSubPort: number;
  targetRatio: number;
}

const SCENARIO_COUNT = 6;
// const getProgressBarColor = (current: number, target: number) => {
//   const percentOfTarget = (current / target) * 100;
//   if (percentOfTarget > 110) return '#ef4444'; // เกินเป้า (แดง)
//   if (percentOfTarget >= 90) return '#10b981'; // ใกล้เคียง/ถึงเป้า (เขียว)
//   return '#3b82f6'; // ยังไม่ถึง (น้ำเงิน)
// };
const AverageCalculatePopup: React.FC<AverageCalculatePopupProps> = ({
  open,
  onClose,
  symbol,
  currentPrice,
  currentShares,
  currentCost,
  useCustomPrice,
  customPrice,
  totalValueInSubPort,
  targetRatio
}) => {
  const STORAGE_KEY = `avg-calc-invests`; // ใช้คีย์เดียวกันสำหรับทุก symbol
  const [invests, setInvests] = useState<number[]>(() => {
    // โหลดจาก localStorage ถ้ามี
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const arr = JSON.parse(saved);
        if (Array.isArray(arr) && arr.length === SCENARIO_COUNT) {
          return arr;
        }
      } catch { }
    }
    return Array(SCENARIO_COUNT).fill(0);
  });
  const [results, setResults] = useState<
    { invest: number; addShares: number; newAvg: number; newRatio: number }[]
  >([]);
  const [livePrice, setLivePrice] = useState<number | null>(null);

  // คริปโต: ราคาเหรียญถูกต่ำกว่า $1 ได้ / จำนวนเหรียญเป็นเศษได้มาก (แสดงผลเท่านั้น)
  const isCrypto = isCryptoSymbol(symbol);
  const unit = isCrypto ? 'เหรียญ' : 'หุ้น';
  const usd = (n: number) => (isCrypto ? formatCryptoUsd(n) : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const qty = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: isCrypto ? 8 : 2 });

  // อัพเดต localStorage ทุกครั้งที่ invests เปลี่ยน
  useEffect(() => {
const priceToUse = useCustomPrice && customPrice !== null 
  ? customPrice 
  : (livePrice && livePrice > 0 ? livePrice : currentPrice);
      const newResults = invests.map((invest) => {
      const addShares = priceToUse > 0 ? invest / priceToUse : 0;
      const newShares = currentShares + addShares;
      const totalCost = currentCost;
      const newTotalShares = currentShares + addShares;
      const newCost = totalCost + invest;
      const newAvg = newTotalShares > 0 ? newCost / newTotalShares : 0;

      // --- คำนวณ Ratio ใหม่สำหรับ Scenario นี้ ---
      const newAssetValue = newTotalShares * priceToUse;
      const newTotalPortValue = totalValueInSubPort + invest;
      const newRatio = newTotalPortValue > 0 ? (newAssetValue / newTotalPortValue) * 100 : 0;

      return { invest, addShares, newAvg, newRatio };
    });
    setResults(newResults);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(invests));
  }, [invests, livePrice, currentShares, currentCost, useCustomPrice, customPrice, currentPrice, totalValueInSubPort]);  // ถ้า popup เปิด ให้ดึงราคาหุ้นล่าสุด

 useEffect(() => {
    if (!open) return;
    let ignore = false;
    async function fetchPrice() {
      // Polygon snapshot ของหุ้นไม่มีราคาเหรียญ → คริปโตใช้ currentPrice (ราคา realtime จาก gateway) แทน
      if (isCryptoSymbol(symbol)) {
        setLivePrice(null);
        return;
      }
      // 1. ส่งครอบด้วย [symbol] เพื่อให้ตรงกับประเภท string[] ที่ฟังก์ชันต้องการ
      const priceMap = await fetchPolygonSnapshotPriceses([symbol]); 
      
      if (!ignore) {
        // 2. ดึงราคาเฉพาะของหุ้นตัวนี้ออกมาจาก Object ถ้าไม่มีให้เปลี่ยนเป็น null
        const price = priceMap[symbol] ?? null; 
        setLivePrice(price);
      }
    }
    fetchPrice();
    return () => {
      ignore = true;
    };
  }, [symbol, open]);
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
      onClick={(e) => {
        // ปิด popup เมื่อคลิกที่พื้นหลัง (overlay) เท่านั้น
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-[#181F2A] text-white rounded-xl p-6 w-full max-w-xl shadow-2xl relative">
        <button
          className="absolute top-3 right-4 text-gray-400 hover:text-red-400 text-2xl"
          onClick={onClose}
          aria-label="close"
        >
          ×
        </button>
        <div className="flex items-center mb-4">
          <span className="text-2xl mr-2">{getStockEmoji(symbol)}</span>
          <span className="text-xl font-bold">{cryptoPairLabel(symbol)}</span>
        </div>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <div className="text-xs text-gray-400">ราคาตลาด ($)</div>
            <div className="bg-[#232B3B] rounded px-3 py-2 font-bold"
              style={{ color: 'var(--text-color)' }}
            >
              {usd(useCustomPrice && customPrice !== null
                ? customPrice
                : livePrice ?? currentPrice
              )}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-400">ค่าเฉลี่ยเดิม ($)</div>
            <div className="bg-[#232B3B] rounded px-3 py-2 text-yellow-300 font-bold">
              {usd(currentCost / (currentShares || 1))}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-400">จำนวน{unit}ที่มีในพอร์ต ({unit})</div>
            <div className="bg-[#232B3B] rounded px-3 py-2 text-green-400 font-bold break-all">{qty(currentShares)}</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm bg-[#232B3B] rounded-lg">
            <thead>
              <tr className="text-gray-300">
                <th className="px-4 py-2 text-left">ยอดซื้อเพิ่ม ($)</th>
                <th className="px-2 py-2 text-left">ค่าเฉลี่ยใหม่/{unit}</th>
                <th className="px-2 py-2 text-left">สัดส่วนพอร์ตใหม่</th>

              </tr>
            </thead>
            <tbody>
              {invests.map((val, idx: any) => {
                const res = results[idx];
                const ratioValue = res?.newRatio || 0;

                // 1. คำนวณว่า "สัดส่วนใหม่" คิดเป็นกี่เปอร์เซ็นต์ของ "เป้าหมาย"
                // เช่น สัดส่วนใหม่ 8% เป้าหมาย 10% -> progressRatio = 80%
                const progressRatio = targetRatio > 0 ? (ratioValue / targetRatio) * 100 : 0;

                // 2. จำกัดความกว้างของหลอดไม่ให้เกิน 100% (เพื่อความสวยงามของ UI)
                const visualWidth = Math.min(progressRatio, 100);
                return (
                  <tr key={idx} className="border-b border-[#2D3748] hover:bg-[#1f293a]">
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        className="w-24 px-2 py-1 rounded bg-[#181F2A] border border-gray-600"
                        value={val === 0 ? '' : val}
                        onChange={(e) => {
                          const arr = [...invests];
                          arr[idx] = Number(e.target.value);
                          setInvests(arr);
                        }}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <div className="font-bold text-yellow-300">${usd(res?.newAvg ?? 0)}
              </div>
                      <div className="text-[10px] text-green-400">+{qty(res?.addShares ?? 0)} {unit}</div>
                    </td>
                    <td className="px-2 py-2 min-w-[180px]">
                      <div className="flex justify-between text-[11px] mb-1">
                        <span className={ratioValue > targetRatio ? 'text-red-400' : 'text-gray-300'}>
                          {ratioValue.toFixed(2)}%
                        </span>
                        <span className="text-gray-500" style={{ color: "var(--text-color)" }}>เป้าหมาย: {targetRatio}%</span>
                      </div>
                      {/* Progress Bar เล็กๆ ในตาราง */}
                      <div className="w-full bg-gray-700 rounded-full h-1.5">
                        <div
                          className="h-1.5 rounded-full transition-all duration-500"
                          style={{
                            width: `${visualWidth}%`,
                            backgroundColor: getProgressBarColor(progressRatio, targetRatio)
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-4 text-xs text-gray-400">
          * ระบบ auto save ทุกครั้งที่แก้ไข
        </div>
      </div>
    </div>
  );
};

export default AverageCalculatePopup;